import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { selectFavoriteAssistants } from '../favorites/favorites.selectors';
import * as fromLayout from '../layout';
import { ChatsHelper } from './chats.helper';
import { ChatNavigationItem } from './chats.interfaces';
import { ChatAssistantProfileComponent } from './components/chat-assistant-profile.component';
import { DocumentViewerSmartComponent } from './components/document-viewer/document-viewer.smart.component';
import { NavigationSmartComponent } from './components/navigation/navigation.smart.component';
import * as fromShared from './shared';
import { ChatsState, chatsFeature } from './store/chats.reducer';

@Component({
  selector: 'squadbox-chats-mobile',
  imports: [
    RouterModule,
    TranslateModule,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.IconButtonDirective,
    DocumentViewerSmartComponent,
    ChatAssistantProfileComponent,
    NavigationSmartComponent,
  ],
  providers: [ChatsHelper],
  template: `
    <div
      class="relative h-dvh w-dvw overflow-hidden"
      [class.bg-neutral-100]="isMobileNavigationOpened()"
    >
      @let assistant = selectedChatAssistant();
      @if (viewMode().includes('doc-viewer')) {
        <squadbox-document-viewer-smart></squadbox-document-viewer-smart>
      } @else if (assistantProfileOpened() && assistant) {
        <squadbox-chat-assistant-profile
          class="row-span-2"
          [selectedAssistant]="assistant"
        ></squadbox-chat-assistant-profile>
      } @else {
        <!-- TopBar Home -->
        @if (isMobileNavigationOpened()) {
          <div
            class="flex flex-row justify-between items-center py-2 px-4 sticky top-0 z-10 text-foreground"
          >
            <div class="flex flex-row items-center gap-4">
              <button
                sdIconButton
                variant="icon"
                color="neutral"
                size="md"
                (click)="toggleSideContainer()"
              >
                <svg sdUseUIBasicIcon="menu"></svg>
              </button>

              <span class="typo-subheader font-semibold">
                {{ 'HOME.TITLE' | translate }}
              </span>
            </div>
          </div>

          <!-- FAB new-convo -->
          <button
            sdIconButton
            variant="filled"
            color="primary"
            size="lg"
            class="fixed bottom-4 right-4 z-20 shadow-md"
            (click)="handleClickNewChatCTA()"
            [style.view-transition-name]="'new-chat-fab'"
          >
            <svg sdUseNavigationIcon="new-chat"></svg>
          </button>
        } @else {
          <!-- ConversationPage / AssistantsPage -->
          <router-outlet />
        }
        <!-- Navigation Mobile -->
        <div
          class="
              mobile-drawer absolute z-10 inset-0
              bg-background rounded-t-3xl
            "
          [class.mobile-drawer--visible]="isMobileNavigationOpened()"
          [style.view-transition-name]="'mobile-drawer-menu'"
        >
          <squadbox-navigation-smart />
        </div>
      }
    </div>
  `,
  styles: [
    `
      .mobile-drawer {
        transition: top 500ms cubic-bezier(0.87, 0, 0.13, 1);
        top: 100%;
      }

      .mobile-drawer--visible {
        top: 64px;
        transition: top 833ms var(--ease-in-spring-15);
      }
    `,
  ],
})
export class ChatsMobileResponsive implements OnInit {
  private readonly routerService = inject(fromShared.RouterService);
  private readonly router = inject(Router);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly assistantsStore = inject(Store);
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );
  private readonly mobileSidebar = inject(fromShared.MobileSidebarService);
  public readonly isNewChat = input.required<boolean>();
  public readonly chatNavigationItems = input.required<ChatNavigationItem[]>();
  private readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );

  public readonly itemClicked = output<ChatNavigationItem>();

  public readonly activeAssistants = this.assistantsStore.selectSignal(
    selectFavoriteAssistants,
  );
  public readonly isMobileSidebarOpened = this.mobileSidebar.isOpened;
  public readonly isMobileNavigationOpened = this.mobileNavigation.isOpened;
  public readonly viewMode = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectViewMode,
  );
  public readonly assistantProfileOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsAssistantProfileOpened,
  );
  public readonly selectedChat = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(
      chatsFeature.selectSelectedChat,
    );

    return {
      ...selectedChat(),
      title: this.chatsHelper.getChatTitle(selectedChat()?.title),
    };
  });
  public readonly selectedChatAssistant = this.assistantsStore.selectSignal(
    fromShared.selectSelectedChatAssistant,
  );

  ngOnInit(): void {
    const selectedChat = this.selectedChat();
    const isEmptyChat = (selectedChat?._count?.messages ?? 0) === 0;
    // Open navigation on mobile when visiting the base URL
    if (this.isMobile() && !this.isMobileNavigationOpened() && isEmptyChat) {
      const currentUrl = this.router.url;

      // Only target empty chat *without* assistant
      if (
        currentUrl === '/chats/conversation' ||
        (currentUrl.startsWith('/chats/conversation/') &&
          !currentUrl.includes('assistantUuid'))
      ) {
        setTimeout(() => {
          void this.mobileNavigation.open();
        }, 500); // wait for the url-sync service normalization to complete
      }
    }
  }

  public async toggleSideContainer(): Promise<void> {
    if (this.isMobileSidebarOpened()) {
      await this.mobileSidebar.close();
    } else {
      await this.mobileSidebar.open();
    }
  }

  public async handleExploreMyAssistantsClicked() {
    await this.mobileNavigation.close();
  }

  public async handleClickNewChatCTA() {
    await this.routerService.navigateToEmptyConversation();
  }
}
